import React, { PropsWithChildren } from 'react';
import useModalV3 from 'Hooks/useModalV3';
import useRouter from 'Hooks/useRouter';
import { Page404V3, Page403V3, Page422V3 } from 'Pages/4XX';
import { Page500V3, Page502V3, Page503V3, Page504V3 } from 'Pages/5XX';
import PageFailedToFetchV3 from 'Pages/FailedToFetch/FailedToFetchV3';
import useAuth from 'Hooks/useAuth';
import NetworkErrorModalV3 from 'Components/modals/NetworkErrorModal/NetworkErrorModalV3';
import { PANTHER_SESSION_REDIRECT_KEY } from 'Source/constants';
import storage from 'Helpers/storage';

export interface LocationErrorState {
  statusCode?: number | 'unknown';
  errorId?: string;
}

const ApiErrorFallbackV3: React.FC<PropsWithChildren> = ({ children }) => {
  const { location } = useRouter<Record<string, string>, LocationErrorState>();
  const { showModal, hideModal } = useModalV3();
  const { isAuthenticated, signOut } = useAuth();

  const errorCode = location.state?.statusCode;

  const showNetworkErrorModal = React.useCallback(() => {
    showModal(<NetworkErrorModalV3 />, { title: 'No Internet Connection' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hideNetworkErrorModal = React.useCallback(() => {
    hideModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    window.addEventListener('offline', showNetworkErrorModal);
    window.addEventListener('online', hideNetworkErrorModal);

    return () => {
      window.removeEventListener('offline', showNetworkErrorModal);
      window.removeEventListener('online', hideNetworkErrorModal);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Protect against case where the token has expired during the time the user was navigating
  // and a page refresh hasn't occurred
  React.useEffect(() => {
    if (errorCode === 401 && isAuthenticated) {
      storage.session.write(PANTHER_SESSION_REDIRECT_KEY, location.pathname);
      signOut();
    }
    // FIXME: look into hook dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode]);

  switch (errorCode) {
    case 404:
      return <Page404V3 />;
    case 403:
      return <Page403V3 />;
    case 422:
      return <Page422V3 />;
    case 500:
      return <Page500V3 />;
    case 502:
      return <Page502V3 />;
    case 503:
      return <Page503V3 />;
    case 504:
      return <Page504V3 />;
    case 'unknown':
      return <PageFailedToFetchV3 />;
    default:
      return children as React.ReactElement;
  }
};

export default ApiErrorFallbackV3;
